import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../common/Button';
import OrderPreview from './OrderPreview';
import { toast } from 'react-toastify';
import { Preset, OrderPlacementRequest, PriceType, LimitPriceType } from '../../types/preset';
import { orderFormSchema, OrderFormData, validateOrderForm } from '../../validation/orderFormSchema';
import { orderService } from '../../services/orderService';
import { useSymbolPrecisions } from '../../hooks/useSymbolPrecisions';
import OrderFormFields from './OrderFormFields'; // Import the new component

// Define minimum order sizes (could be fetched from API in a real implementation)
const MIN_ORDER_SIZES: Record<string, number> = {
  'BTC-PERP': 0.001,
  'ETH-PERP': 0.01,
  'SOL-PERP': 0.1
};

interface OrderFormProps {
  selectedPreset: Preset | null;
  onOrderSubmitted?: (success: boolean, message: string) => void;
  selectedSymbol?: string; // Add optional prop for dashboard symbol
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%; /* Fill the section */
`;

const GeneralErrorMessage = styled.div`
    color: #f87171; // Tailwind red-400
    font-size: 0.8rem;
    margin-top: 0.5rem;
    text-align: center;
`;

// Define default form values matching the schema structure
const defaultValues: Partial<OrderFormData> = {
    symbol: '',
    side: 'buy',
    order_type: 'market',
    amount: 0,
    amount_type: 'base',
    margin_mode: 'cross',
    leverage: 10,
    limit_price: null,
    stop_price: null,
    limit_price_type: LimitPriceType.ABSOLUTE,
    price_offset: 0,
    flags: { post_only: false, reduce_only: false },
    stop_loss: { enabled: false, trigger_price_value: null, price_type: null, limit_price_value: null, limit_price_type: null },
    take_profit: { enabled: false, trigger_price_value: null, price_type: null, limit_price_value: null, limit_price_type: null },
    layered: { parts: 5, step: 0.5 },
    preset_type: 'preset',
    name: 'ManualOrder',
};

const OrderForm: React.FC<OrderFormProps> = ({ 
  selectedPreset, 
  onOrderSubmitted, 
  selectedSymbol // Destructure the new prop
}) => {
  // Get symbol data with precisions
  const { 
    symbols, 
    precisions, 
    isLoading: loadingSymbols, 
    error: symbolError,
    formatPrice,
    formatSize,
    getPriceStep,
    getSizeStep
  } = useSymbolPrecisions();

  // State for submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

  // React Hook Form setup
  const { 
    register, // Keep register if needed for top-level form elements
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<OrderFormData>({ 
    resolver: zodResolver(orderFormSchema) as any,
    defaultValues: defaultValues,
  });

  // Watch all fields for preview component
  const watchedValues = watch(); 
  const watchedOrderType = watch('order_type'); // Needed for logic below
  const watchedLimitPriceType = watch('limit_price_type'); // Needed for logic below

  // Set a default symbol based on prop or available symbols
  useEffect(() => {
    if (symbols.length > 0 && !watchedValues.symbol) {
      let defaultSymbolToSet = symbols[0]; // Default to first symbol

      if (selectedSymbol) { // If a symbol is passed from parent
        if (symbols.includes(selectedSymbol)) {
          defaultSymbolToSet = selectedSymbol;
        } else {
          console.warn(`OrderForm: Provided selectedSymbol '${selectedSymbol}' not in available list. Using ${defaultSymbolToSet}.`);
        }
      }
      
      setValue('symbol', defaultSymbolToSet, { shouldValidate: true });
    }
  // Update dependency array to include selectedSymbol
  }, [symbols, watchedValues.symbol, setValue, selectedSymbol]);

  // Effect to populate form from selectedPreset
  useEffect(() => {
    if (selectedPreset) {
      console.log("Populating form with preset:", selectedPreset.name);
      const presetFormData: Partial<OrderFormData> = {
          ...selectedPreset,
          amount: Number(selectedPreset.amount) || 0,
          leverage: Number(selectedPreset.leverage) || 10,
          limit_price: selectedPreset.limit_price ? Number(selectedPreset.limit_price) : null,
          limit_price_type: (selectedPreset.limit_price_type === LimitPriceType.ABSOLUTE || 
                             selectedPreset.limit_price_type === LimitPriceType.BEST_BID || 
                             selectedPreset.limit_price_type === LimitPriceType.BEST_ASK) 
                            ? selectedPreset.limit_price_type 
                            : LimitPriceType.ABSOLUTE,
          price_offset: selectedPreset.price_offset ? Number(selectedPreset.price_offset) : 0,
          stop_price: selectedPreset.stop_price ? Number(selectedPreset.stop_price) : null,
          stop_loss: {
              enabled: selectedPreset.stop_loss?.enabled ?? false,
              trigger_price_value: selectedPreset.stop_loss?.trigger_price_value ? Number(selectedPreset.stop_loss.trigger_price_value) : null,
              price_type: null,
              limit_price_value: selectedPreset.stop_loss?.limit_price_value ? Number(selectedPreset.stop_loss.limit_price_value) : null,
              limit_price_type: null,
          },
          take_profit: {
              enabled: selectedPreset.take_profit?.enabled ?? false,
              trigger_price_value: selectedPreset.take_profit?.trigger_price_value ? Number(selectedPreset.take_profit.trigger_price_value) : null,
              price_type: null,
              limit_price_value: selectedPreset.take_profit?.limit_price_value ? Number(selectedPreset.take_profit.limit_price_value) : null,
              limit_price_type: null,
          },
          layered: {
              parts: selectedPreset.layered?.parts ? Number(selectedPreset.layered.parts) : 5,
              step: selectedPreset.layered?.step ? Number(selectedPreset.layered.step) : 0.5,
          },
          flags: {
              post_only: selectedPreset.flags?.post_only ?? false,
              reduce_only: selectedPreset.flags?.reduce_only ?? false,
          },
          preset_type: 'preset' as const,
          name: selectedPreset.name,
      };

       if (symbols.length > 0 && !symbols.includes(selectedPreset.symbol)) {
             console.warn(`Preset symbol ${selectedPreset.symbol} not in available list. Using ${symbols[0]}.`);
             presetFormData.symbol = symbols[0];
       }

      reset(presetFormData);
      setSubmitError(null);
      setValidationErrors({});
    } else {
      console.log("Selected preset cleared");
      // reset(defaultValues); 
    }
  }, [selectedPreset, reset, symbols]);

  // Re-calculate necessary flags for submission logic
  const showLimitPrice = watchedOrderType === 'limit' || watchedOrderType === 'stop_limit';
  const showOffsetInput = showLimitPrice && (watchedLimitPriceType === LimitPriceType.BEST_BID || watchedLimitPriceType === LimitPriceType.BEST_ASK);
  const showStopPrice = watchedOrderType === 'stop_market' || watchedOrderType === 'stop_limit';
  const showTradingFields = !['cancel_all', 'cancel_bids', 'cancel_asks'].includes(watchedOrderType as string);

  // --- Submission Handler ---
  const onSubmit = async (data: OrderFormData) => {
    setSubmitError(null);
    setValidationErrors({});
    
    // Perform custom validation
    const validation = validateOrderForm(data, precisions, MIN_ORDER_SIZES);
    if (!validation.success) {
      setValidationErrors(validation.errors);
      Object.values(validation.errors).flat().forEach(error => {
        toast.error(error);
      });
      if (onOrderSubmitted) {
        onOrderSubmitted(false, "Validation failed: " + Object.values(validation.errors).flat().join(", "));
      }
      return;
    }
    
    setIsSubmitting(true);

    // --- Construct Payload (logic remains largely the same, uses `data` from RHF) ---
    const orderData: OrderPlacementRequest = {
      symbol: data.symbol,
      side: data.side,
      order_type: data.order_type,
      amount: data.amount,
      amount_type: data.amount_type,
      margin_mode: data.margin_mode,
      leverage: data.leverage,
      limit_price: (showLimitPrice && data.limit_price_type === LimitPriceType.ABSOLUTE && data.limit_price) ? data.limit_price : undefined,
      limit_price_type: showLimitPrice ? data.limit_price_type : undefined,
      price_offset: showOffsetInput && data.price_offset ? data.price_offset : undefined,
      stop_price: showStopPrice && data.stop_price ? data.stop_price : undefined,
      stop_loss: data.stop_loss?.enabled ? {
          enabled: true,
          trigger_price_value: data.stop_loss.trigger_price_value ? Number(data.stop_loss.trigger_price_value) : undefined
      } : { enabled: false },
      take_profit: data.take_profit?.enabled ? {
          enabled: true,
          trigger_price_value: data.take_profit.trigger_price_value ? Number(data.take_profit.trigger_price_value) : undefined
      } : { enabled: false },
       layered: data.order_type === 'layered' && data.layered ? {
           parts: Number(data.layered.parts),
           step: Number(data.layered.step),
       } : undefined,
      flags: data.flags,
    };

    // --- API Call Logic (remains the same) ---
    try {
      console.log('Submitting Order:', orderData);
      if (['cancel_all', 'cancel_bids', 'cancel_asks'].includes(data.order_type)) {
        const cancelResult = await orderService.cancelOrders({ 
          symbol: orderData.symbol, 
          side: data.order_type === 'cancel_bids' ? 'buy' : data.order_type === 'cancel_asks' ? 'sell' : undefined 
        });
        console.log('Cancel Result:', cancelResult);
        toast.success('Cancel request submitted.');
        if (onOrderSubmitted) {
          onOrderSubmitted(true, `Cancelled orders for ${orderData.symbol}`);
        }
      } else {
        const result = await orderService.placeOrder(orderData);
        console.log('Order Result:', result);
        toast.success(`Order placed successfully! ${result.order_id ? `Order ID: ${result.order_id}` : ''}`);
        if (onOrderSubmitted) {
          onOrderSubmitted(true, `Order placed successfully${result.order_id ? ` (ID: ${result.order_id})` : ''}`);
        }
        // reset(defaultValues); 
      }
    } catch (error: any) {
      console.error('Order submission failed:', error);
      const errorMsg = error?.response?.data?.detail || error.message || 'An unknown error occurred.';
      setSubmitError(errorMsg);
      toast.error(`Order failed: ${errorMsg}`);
      if (onOrderSubmitted) {
        onOrderSubmitted(false, errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Render --- 
  if (loadingSymbols) {
    return <div>Loading symbols...</div>; // Add loading indicator
  }

  if (symbolError) {
    return <div>Error loading symbols: {symbolError.message}</div>; 
  }

  // Make sure we have symbol data before rendering the form fields
  if (!watchedValues.symbol || !precisions[watchedValues.symbol]) {
    return <div>Waiting for symbol data...</div>; // Or a more sophisticated loader
  }
  
  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      {/* Pass RHF methods and symbol data down */}
      <OrderFormFields
        control={control}
        errors={errors}
        register={register}
        watch={watch}
        precisions={precisions}
        symbols={symbols}
        getPriceStep={getPriceStep}
        getSizeStep={getSizeStep}
        loadingSymbols={loadingSymbols}
        validationErrors={validationErrors}
        isSubmitting={isSubmitting}
      />

      {/* General form error message */}
      {submitError && <GeneralErrorMessage>{submitError}</GeneralErrorMessage>}
      
      {/* Preview component */}
      {/* <OrderPreview formData={watchedValues} /> */}
      
      {/* Submit button moved to OrderFormFields */}
    </FormContainer>
  );
};

export default OrderForm; 